import { useContext, useMemo } from 'react'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import GeoContext from 'contexts/geoContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { getDurationPackageOptions } from 'selectors/offerPage/offerPageSelectors'
import { sortPackageOptions } from 'lib/offer/offerPageUtil'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import useLoyaltyUserBenefitView from 'loyalty/hooks/useLoyaltyUserBenefitView'
import useLoyaltyPointsEarnCalculator from 'loyalty/hooks/useLoyaltyPointsEarnCalculator'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import determineNextLoyaltyTier from 'loyalty/lib/determineNextLoyaltyTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { min } from 'lib/array/arrayUtils'
import { formatInlineList } from 'lib/format/formatList'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { getAvailablePackagesByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'

interface Params {
  room?: OfferPageRoom
}

interface SuggestedPackageResult {
  option: App.OfferPackageOptionView
  message: string
}

/**
 * Determines the best package to recommend based on the following prioritisation:
 * 1. If the user has gold loyalty status, offer the cheapest option eligible for a room upgrade
 * 2. If a user is close to upgrading their loyalty tier, highlight the cheapest option that will upgrade them
 * 3. The most popular option from the list
 *
 * In all cases the room must be available and valid for the selected occupancy.
 */
function useSuggestedPackageOption({ room }: Params = {}): SuggestedPackageResult | undefined {
  const offerPageState = useContext(OfferPageStateContext)
  const { currentRegionCode } = useContext(GeoContext)
  const { rooms: allRooms, duration, offer } = offerPageState

  const packageOptionsAllRooms = useAppSelector(state => getDurationPackageOptions(offerPageState, state))
  const packageOptionsByRoom = useAppSelector(state => getAvailablePackagesByRoom(offerPageState, state))

  const packageOptions = room ? packageOptionsByRoom[room.id] : packageOptionsAllRooms
  const rooms = room ? [room] : allRooms

  const loyaltyAccount = useAppSelector(state => state.loyalty.account.data)

  const hotelUpgradeBenefit = useLoyaltyUserBenefitView('hotel_upgrade')
  const hasRoomUpgradeBenefit = !!hotelUpgradeBenefit && hotelUpgradeBenefit.availability > 0

  const sortedPackageOptions = useMemo(() => {
    return sortPackageOptions(packageOptions)
  }, [packageOptions])

  // Priority 1: Gold loyalty + room upgrade eligible
  const isGoldOrHigher = loyaltyAccount?.tier === 'gold' || loyaltyAccount?.tier === 'platinum'

  const cheapestRoomUpgradePackage = useMemo(() => {
    if (!isGoldOrHigher || !hasRoomUpgradeBenefit) return undefined

    const upgradeEligiblePackages = sortedPackageOptions.filter(
      option => !!option.package.loyalty.upgradeTargetsUniqueKeys?.room && option.capacityValid && option.hotelAvailable,
    )

    return min(upgradeEligiblePackages, option => option.hotelPrice ?? Number.MAX_SAFE_INTEGER)
  }, [isGoldOrHigher, hasRoomUpgradeBenefit, sortedPackageOptions])

  // Priority 2: Tier upgrade potential - calculate status credits for all packages
  const nextTier = loyaltyAccount ? determineNextLoyaltyTier(loyaltyAccount.tier) : undefined
  const creditsNeeded = loyaltyAccount && nextTier
    ? Math.max(loyaltyAccount.totalCreditsForTierUpgrade - loyaltyAccount.statusCredits, 0)
    : 0
  const nightsNeeded = loyaltyAccount && nextTier
    ? Math.max(loyaltyAccount.numberOfNightsForTierUpgrade - loyaltyAccount.numberOfNights, 0)
    : 0

  // Check if selected duration can satisfy nights requirement
  const durationSatisfiesNights = duration >= nightsNeeded && nightsNeeded > 0

  // If they're choosing a room in individual room mode, we can't tell if this one selection will make them eligible for an upgrade
  const isSelectingForAllRooms = !room

  // Only calculate status credits if we need to check for tier upgrade
  const couldBeEligibleForUpgrade = !cheapestRoomUpgradePackage && durationSatisfiesNights && creditsNeeded > 0 && isSelectingForAllRooms && !!nextTier

  const calculationRequests = useMemo(() => {
    if (!couldBeEligibleForUpgrade || !offer) return []

    return sortedPackageOptions
      .filter(option => option.capacityValid && option.hotelAvailable)
      .map(option => generateLoyaltyPointsCalculatorHotelOptions(offer, {
        packageView: option,
        currentRegionCode,
        nights: duration,
      }))
  }, [couldBeEligibleForUpgrade, offer, sortedPackageOptions, currentRegionCode, duration])

  const { pointsCalculations, totalPointsCalculation } = useLoyaltyPointsEarnCalculator(calculationRequests, {
    skipAll: !couldBeEligibleForUpgrade,
  })

  const isLoadingCredits = couldBeEligibleForUpgrade && (totalPointsCalculation.fetching || totalPointsCalculation.initial)

  // Find cheapest package that earns enough status credits
  const cheapestTierUpgradePackage = useMemo(() => {
    if (!couldBeEligibleForUpgrade || isLoadingCredits || !nextTier) return undefined

    const availablePackages = sortedPackageOptions.filter(option => option.capacityValid && option.hotelAvailable)

    // Match calculations back to packages by index
    for (let i = 0; i < availablePackages.length; i++) {
      const calculation = pointsCalculations[i]
      if (calculation?.data) {
        const totalStatusCredits = calculation.data.statusCredits + (calculation.data.bonusStatusCredits ?? 0)
        if (totalStatusCredits >= creditsNeeded) {
          return { package: availablePackages[i], nextTier }
        }
      }
    }

    return undefined
  }, [couldBeEligibleForUpgrade, isLoadingCredits, nextTier, sortedPackageOptions, pointsCalculations, creditsNeeded])

  // Priority 3: Fallback to most popular option (with lowest price as tiebreaker).
  // Note: Booking count is only available at the room type level, so effectively this is the
  // cheapest option of the most popular room type.
  const popularOption = useMemo(() => {
    const availablePackages = sortedPackageOptions.filter(option => option.capacityValid && option.hotelAvailable)
    if (availablePackages.length <= 1) {
      return undefined // It would be silly to highlight the most popular option if there was only one option...
    }
    const highestBookingCount = Math.max(...availablePackages.map(p => p.package.roomType.stats.booked))
    const mostPopularPackages = availablePackages.filter(p => p.package.roomType.stats.booked === highestBookingCount)
    return min(mostPopularPackages, option => option.hotelPrice ?? Number.MAX_SAFE_INTEGER)
  }, [sortedPackageOptions])

  // Determine which priority to use
  let result: SuggestedPackageResult | undefined = undefined

  if (cheapestRoomUpgradePackage) {
    result = {
      option: cheapestRoomUpgradePackage,
      message: 'Eligible for a free room upgrade',
    }
  }
  else if (isLoadingCredits) {
    result = undefined
  }
  else if (cheapestTierUpgradePackage) {
    const nextTierName = getLoyaltyTierDisplayName(cheapestTierUpgradePackage.nextTier)
    result = {
      option: cheapestTierUpgradePackage.package,
      message: `Book now to become a ${nextTierName} Société member!`,
    }
  }
  else if (popularOption) {
    const occupantsMessage = formatInlineList(formatOccupantsShort(rooms).split(', '), 'conjunction', { style: 'long' })
    result = {
      option: popularOption,
      message: occupantsMessage ? `Popular for ${occupantsMessage}` : '',
    }
  }

  return result
}

export default useSuggestedPackageOption
